import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import { layout, space, flex } from 'styled-system';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';

const defaultHeight = '40px';

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: ${p => (p.inline ? 'row' : 'column')};
  justify-content: flex-start;
  align-items: ${p => (p.inline ? 'baseline' : 'flex-start')};
  ${layout};
  ${space};
  ${flex};
`;

// TODO: We might want to define this as its own library component at some point?
const Label = styled.label`
  font-family: ${p => p.theme.fontFamily};
  font-size: 1.14em;
  font-weight: bold;
  line-height: 1.5;
  margin: ${p => (p.inline ? '0' : '10px')} 0;
  letter-spacing: 0.3px;
  color: ${p => p.theme.colors.text.primary};
  padding-left: ${p => (p.inline ? '0' : '2px')};
  padding-right: ${p => (p.inline ? '10px' : '0')};
`;

const Error = styled.label`
  font-family: ${p => p.theme.fontFamily};
  font-size: 0.85em;
  letter-spacing: 0.3px;
  padding-left: 2px;
  color: ${p => p.theme.colors.aux.red};
`;

const InputErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${p => p.inputHeight || defaultHeight};
  width: ${p => p.inputHeight || defaultHeight};
  box-sizing: border-box;
  border: 1px solid ${p => p.theme.colors.aux.darkGrey};
  fill: inherit;
  & > svg {
    height: 20px;
    width: 20px; /* Required for IE11 bug */
  }
`;

const LeftIconWrapper = styled(IconWrapper)`
  order: 0;
  border-right: none;
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
`;

const RightIconWrapper = styled(IconWrapper)`
  order: 2;
  border-left: none;
  border-top-right-radius: 2px;
  border-bottom-right-radius: 2px;
`;

const textPadding = active => {
  // accounts for changing border width
  const value = active ? '9px' : '10px';
  return css`
    /* Remove extra padding when an icon is present without an inner border */
    padding-left: ${p => (p.leftIcon && !p.leftIconBorder ? '0' : value)};
    padding-right: ${p => (p.rightIcon && !p.rightIconBorder ? '0' : value)};
  `;
};

const activeSelector =
  '&:focus:not([disabled]):not([readonly]), &:hover:not([disabled]):not([readonly])';

const border = css`
  border-width: 1px;
  border-style: solid;
  border-top-left-radius: ${p => (p.leftIcon ? '0' : '2px')};
  border-bottom-left-radius: ${p => (p.leftIcon ? '0' : '2px')};
  border-top-right-radius: ${p => (p.rightIcon ? '0' : '2px')};
  border-bottom-right-radius: ${p => (p.rightIcon ? '0' : '2px')};
  /* Remove icon inner borders as necessary */
  ${p => p.leftIcon && !p.leftIconBorder && 'border-left: none'};
  ${p => p.rightIcon && !p.rightIconBorder && 'border-right: none'};

  /* both input and icon wrappers */
  &,
  & ~ ${IconWrapper} {
    border-color: ${p =>
      p.error
        ? p.theme.colors.aux.red
        : p.ghost
        ? 'transparent'
        : p.theme.colors.aux.darkGrey};
  }

  ${activeSelector} {
    /* both input and icon wrappers */
    &,
    & ~ ${IconWrapper} {
      border-width: 2px;
      border-color: ${p => p.theme.colors.secondary};
    }
  }
`;

const RawInput = styled.input`
  flex: 1 1 auto;
  order: 1;
  height: ${p => p.height || defaultHeight};
  box-sizing: border-box;
  min-width: 15px;
  background: ${p =>
    p.ghost
      ? 'inherit'
      : p.readOnly
      ? p.theme.colors.aux.offWhite
      : p.theme.colors.aux.white};
  color: ${p => p.theme.colors.text.primary};
  font-family: ${p => p.theme.fontFamily};
  font-size: 1em;
  outline: none;
  ${border}
  ${textPadding(false)}

  & ~ ${IconWrapper} {
    fill: ${p => (p.error ? p.theme.colors.aux.red : p.theme.colors.primary)};
    background: ${p =>
      p.readOnly ? p.theme.colors.aux.offWhite : p.theme.colors.aux.white};
  }

  ${activeSelector} {
    & ~ ${IconWrapper} {
      fill: ${p => p.theme.colors.primary};
    }
    ${textPadding(true)}
  }

  &:disabled {
    cursor: not-allowed;
    background: ${p => (p.ghost ? 'inherit' : p.theme.colors.aux.offWhite)};
    & ~ ${IconWrapper} {
      cursor: not-allowed;
      background: ${p => p.theme.colors.aux.offWhite};
      fill: ${p => p.theme.colors.aux.darkGrey};
    }
  }
`;

class Input extends PureComponent {
  focus = () => {
    this.input.focus();
  };

  render() {
    const {
      label,
      inline,
      rightIcon,
      rightIconBorder,
      leftIcon,
      leftIconBorder,
      readOnly,
      error,
      height,
      flex,
      width,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      size,
      overflow,
      overflowX,
      overflowY,
      m,
      mt,
      mr,
      mb,
      ml,
      mx,
      my,
      p,
      pt,
      tr,
      pb,
      pl,
      px,
      py,
      ...props
    } = this.props;

    return (
      <OuterWrapper
        inline={inline}
        {...{
          flex,
          size,
          width,
          minWidth,
          minHeight,
          maxWidth,
          maxHeight,
          overflow,
          overflowX,
          overflowY,
          // display, // already hard-coded (although this is one of styled-system LayoutProps)
          m,
          mt,
          mr,
          mb,
          ml,
          mx,
          my,
          p,
          pt,
          tr,
          pb,
          pl,
          px,
          py,
        }}
      >
        {label && <Label inline={inline}>{label}</Label>}
        <InputErrorWrapper>
          <InputWrapper>
            <RawInput
              {...props}
              ref={elem => (this.input = elem)}
              error={error}
              readOnly={readOnly}
              leftIcon={!!leftIcon}
              leftIconBorder={leftIconBorder}
              rightIcon={!!rightIcon}
              rightIconBorder={rightIconBorder}
              height={height}
            />
            {leftIcon && (
              <LeftIconWrapper inputHeight={height} onClick={this.focus}>
                {leftIcon}
              </LeftIconWrapper>
            )}
            {rightIcon && (
              <RightIconWrapper inputHeight={height} onClick={this.focus}>
                {rightIcon}
              </RightIconWrapper>
            )}
          </InputWrapper>
          {error && <Error>{error}</Error>}
        </InputErrorWrapper>
      </OuterWrapper>
    );
  }
}

Input.propTypes = {
  /**
   * Label for the input
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * If a label is provided, render it inline
   */
  inline: PropTypes.bool,
  /**
   * Icon to show on the right side
   */
  rightIcon: PropTypes.node,
  /**
   * If a rightIcon is provided, render a border between the input and it
   */
  rightIconBorder: PropTypes.bool,
  /**
   * Icon to show on the left side
   */
  leftIcon: PropTypes.node,
  /**
   * If a leftIcon is provided, render a border between the input and it
   */
  leftIconBorder: PropTypes.bool,
  /**
   * Error state or error message
   */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Make the input blend into background when not focused (transparent border)
   */
  ghost: PropTypes.bool,
  /**
   * Set the input to read-only
   */
  readOnly: PropTypes.bool,
  /**
   * Set the input to disabled
   */
  disabled: PropTypes.bool,
  /**
   * Change event handler
   */
  onChange: PropTypes.func,
  ...propTypes.layout,
  ...propTypes.space,
  ...propTypes.flexbox,
};

export default Input;
